import {
  Root as DialogRoot,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Trigger,
} from '@radix-ui/react-dialog';
import {
  Image,
  withStory,
  WithStoryProps,
} from '@storyofams/storyblok-toolkit';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import SbEditable from 'storyblok-react';

import { Button, Heading, Mesh, Seo } from '~/components';
import { BannerDynamic } from '~/components/Banners/BannerDynamic';
import { DynamicForm } from '~/components/Blocks/DynamicForm';
import { smallFeatureIcons } from '~/components/Blocks/FeatureSection';
import { Form } from '~/components/Forms';
import {
  addFormFieldsToStory,
  classNames,
  enhancedStaticProps,
  getLinkProps,
} from '~/lib';
import { withLayout } from '~/lib/withLayout';

const LogoSection = dynamic(() => import('~/components/Blocks/LogoSection'), {
  ssr: false,
});

export interface PartnerPageProps extends WithStoryProps {}

const PartnerPage = ({ story }: PartnerPageProps) => {
  const content = story?.content;

  return (
    <>
      <Seo story={story} />

      <SbEditable content={content}>
        <div className="pt-32 bg-yellow-50 mb-20 md:mb-24">
          <div className="max-w-6xl mx-auto px-4 flex flex-col relative z-20">
            <div className="max-w-[600px]">
              <Heading as="h1" variant="h2" className=" text-5xl !font-medium">
                {content?.title}
              </Heading>
              <p className="text-grey-600 mt-8 md:mt-6 text-xl">
                {content?.subtext}
              </p>
              <div className="flex flex-col md:flex-row mt-8 mb-20 md:mb-0">
                {!!content?.button && content?.button_action === 'modal' ? (
                  <DialogRoot>
                    <Trigger asChild>
                      <Button variant="dark">{content?.button}</Button>
                    </Trigger>

                    <Portal>
                      <Overlay className="fixed inset-0 overflow-y-auto z-50 bg-black/30">
                        <div className="flex min-h-full items-center justify-center p-4">
                          <Content className="mx-auto w-full max-w-sm md:max-w-lg bg-white px-5 py-6 sm:p-8 rounded-2xl relative">
                            <Title className="text-2xl font-medium leading-tight">
                              {content?.modal_form?.[0]?.form_title}
                            </Title>
                            <Description className="text-sm md:text-base text-gray-700 mt-3 md:mt-5">
                              {content?.modal_form?.[0]?.form_subtitle}
                            </Description>

                            <Form
                              className="mt-6"
                              content={content?.modal_form?.[0]}
                            />
                          </Content>
                        </div>
                      </Overlay>
                    </Portal>
                  </DialogRoot>
                ) : (
                  !!content?.button && (
                    <Button
                      variant="dark"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        document
                          .querySelector(content?.button_link?.url)
                          ?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="!py-3"
                    >
                      {content.button}
                    </Button>
                  )
                )}
                {!!content?.button_2 && (
                  <Button
                    variant="white"
                    href={getLinkProps(content?.button_2_link)}
                    className="mt-3 md:mt-0 md:ml-2 !py-3"
                  >
                    {content.button_2}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="relative h-20 md:h-40 flex flex-col overflow-hidden z-0 -mt-16">
            <div className="h-full bottom-0 bg-gradient-to-b from-yellow-50 relative z-10" />
            <Mesh className="absolute bottom-0 w-[1140px] min-w-full left-1/2 transform -translate-x-1/2 xl:w-full z-0 opacity-70" />
          </div>
        </div>

        <div className="md:max-w-6xl mx-auto my-20 md:my-32">
          {!!content.logo_section[0] && (
            <LogoSection content={content?.logo_section[0]} />
          )}
        </div>

        <div className="max-w-6xl px-4 mx-auto mb-32 snap-y snap-mandatory">
          {content?.scroll_panel?.map((panel, i) => {
            const Icon = smallFeatureIcons.find(
              (feature) => feature?.value === panel?.icon,
            )?.icon;

            return (
              <div
                key={panel?._uid}
                className={classNames(
                  'w-full mb-20 lg:mb-32 last:mb-0 snap-center flex flex-col lg:justify-between lg:items-center',
                  i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse',
                )}
              >
                <div className="lg:max-w-[464px]">
                  {Icon && (
                    <div className="w-12 h-12 rounded-full bg-grey-100 mb-4 flex items-center justify-center">
                      <Icon className="w-5" />
                    </div>
                  )}
                  <Heading variant="h5" as="h2" className="text-grey-800">
                    {panel?.title}
                  </Heading>
                  <div className="mt-3 text-lg text-grey-700 mb-10">
                    {panel?.subtext}
                  </div>
                </div>
                <div
                  className={classNames(
                    'lg:max-w-[548px] mx-auto lg:mx-0 w-full',
                    i % 2 === 0 ? 'lg:mr-4' : 'lg:mr-4',
                  )}
                >
                  <Image
                    alt={panel?.image?.alt || ''}
                    src={panel?.image?.filename}
                    fluid={986}
                    fit="cover"
                    showPlaceholder={false}
                    width="100%"
                    className="rounded-2xl w-full"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="max-w-6xl mx-auto px-4 mb-40">
          <BannerDynamic content={content?.banner?.content} />
        </div>

        {!!content?.partner_form?.[0] && (
          <DynamicForm content={content?.partner_form?.[0]} />
        )}
      </SbEditable>
    </>
  );
};

export default withLayout(withStory(PartnerPage));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ sdk }) => {
    let story;
    let notFound = false;

    try {
      story = (await sdk.partnerPageItem({ slug: 'partners' })).PartnerpageItem;
      story = await addFormFieldsToStory(story);
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);
